import styled from "@emotion/styled"
import { PButton, PScroller } from "@porsche-design-system/components-react"
import { spacingFluidSm, spacingStaticSm } from "@porsche-design-system/components-react/emotion"
import type { ReactNode } from "react"
import { shouldForwardProp } from "src/common/shouldForwardProp"
import { Skeleton } from "../skeleton"

interface ActiveFiltersProps {
	onToggleFilters: () => void
	scrollable?: boolean
	search: ReactNode
	tagList?: ReactNode
	toggleFiltersLabel: ReactNode
	value: Record<string, unknown>
	withResetButtonColumn?: boolean
}

export const ActiveFilters = ({
	onToggleFilters,
	scrollable,
	search,
	tagList,
	toggleFiltersLabel,
	value,
	withResetButtonColumn,
}: ActiveFiltersProps) => {
	const hasTagList = tagList !== undefined && tagList !== null
	const SkeletonContainer = withResetButtonColumn ? TagListSkeletonContainerWithResetButton : TagListSkeletonContainer
	const skeletonTags = Object.entries(value)
		.flatMap(([key, val]) => (key !== "search" ? val : undefined))
		.map((val, i) => (val ? <Skeleton.TagDismissible key={i} /> : null))

	return (
		<Layout $scrollable={scrollable}>
			<PButton type="button" variant="secondary" icon="filter" hideLabel onClick={onToggleFilters}>
				{toggleFiltersLabel}
			</PButton>
			{search}

			{hasTagList ? (
				scrollable ? (
					<StyledScroller>
						<TagListWrapper data-testid="filter-tags">{tagList}</TagListWrapper>
					</StyledScroller>
				) : (
					tagList
				)
			) : (
				<SkeletonContainer>{skeletonTags}</SkeletonContainer>
			)}
		</Layout>
	)
}

const Layout = styled("div", { shouldForwardProp })<{ $scrollable?: boolean }>`
	display: flex;
	flex-direction: row;
	gap: ${spacingFluidSm};
	flex-wrap: ${({ $scrollable }) => ($scrollable ? "nowrap" : "wrap")};
	justify-items: start;
	align-items: end;
`

const StyledScroller = styled(PScroller)`
	> * {
		white-space: nowrap;
	}
`

const TagListWrapper = styled.div`
	display: inline-flex;
	gap: ${spacingFluidSm};
	flex-wrap: nowrap;
`

const TagListSkeletonContainer = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: ${spacingStaticSm};
	grid-column: 1 / -1;
	min-height: 54px; /* height of PTagDismissible to prevent jump when PTagDismissible is lazy loaded */
`

const TagListSkeletonContainerWithResetButton = styled(TagListSkeletonContainer)`
	+ * {
		grid-column: 1 / -1;
	}
`
